import React, {
  useContext,
  useState,
  KeyboardEvent,
  useEffect
} from 'react'

import { GridItem } from '@zuto/core/src/grid'
import { RadioButton } from '@zuto/atoms'
import { useCallbackOnce } from '@zuto/utilities'
import QuestionComponentProps from '../../../Questions/question-component-props'
import QuestionKey from '../../../Questions/question-key'
import { ApplicationFormContext } from '../../ApplicationForm/ApplicationFormContext'
import AppFormQuestion from '../../AppFormQuestion'
import withQuestion from '../../HOC/withQuestion'
import withFade from '../../HOC/withFade'
import {
  trackMultipleInputChoice,
  determineRadioVariant,
  handlePrevButtonClick,
  questionFontStyle
} from '../utils'
import NavigationButtons from '../../NavigationButtons/NavigationButtons'
import { SimpleDrivingLicenceOptions } from './DrivingLicenseOptions'
import { PrefillReviewPageApplicationFormContext } from '../../ApplicationForm/PrefillReviewPageApplicationFormContext'
import ToggleButtonGroup from '@zuto/atoms/src/Mui/ToogleButtonGroup/ToggleButtonGroup'
import { useAutoAdvance } from '../../../hooks/useAutoAdvance'

const DoubleColumnRadioDrivingLicenceQuestion = ({
  existingAnswers,
  disablePrevious,
  updateFollowingPrefillStep
}: QuestionComponentProps): JSX.Element => {
  const { setQuestionName, hasNavigatedFromRequiredClick } = useContext(PrefillReviewPageApplicationFormContext)
  const appFormContext = useContext(ApplicationFormContext)
  const { featureSwitches } = appFormContext
  const {
    removeContinueCtaV1,
    reviewPageV3,
    questionCopyChangeV1,
    msmThemeV1
  } = featureSwitches ?? {}
  const [answer, setAnswer] = useState(existingAnswers)
  const [nextButtonCb] = useCallbackOnce()
  const [previousButtonCb] = useCallbackOnce()
  const autoAdvance = useAutoAdvance(QuestionKey.DRIVING_LICENCE)

  useEffect(() => {
    // Used for logging purposes in PrefillReviewPageApplicationForm since questionConfig is not available
    if (reviewPageV3) {
      setQuestionName('drivingLicence')
    }
  }, [])

  const defaultAnswer = 'Full UK Driving Licence'
  const preselectedValue = !removeContinueCtaV1 ? defaultAnswer : undefined

  const nextQuestion = () => {
    trackMultipleInputChoice({ label: `drivingLicence+${answer ?? defaultAnswer}` })
    appFormContext.dispatch({
      type: 'QUESTION_ANSWERED',
      name: QuestionKey.DRIVING_LICENCE,
      value: (answer ?? defaultAnswer)
    })
    // Used for Navigation on Prefill Review Page
    if (updateFollowingPrefillStep) {
      if (reviewPageV3 && hasNavigatedFromRequiredClick) {
        updateFollowingPrefillStep(true, true, false)
      }
      if (!hasNavigatedFromRequiredClick) {
        updateFollowingPrefillStep(true, false, false)
      }
    }
  }

  const previousQuestion = () => {
    handlePrevButtonClick(
      appFormContext.dispatch,
      { name: QuestionKey.DRIVING_LICENCE, value: (answer ?? preselectedValue) }
    )
    // Used for Navigation on Prefill Review Page
    if (updateFollowingPrefillStep) {
      if (reviewPageV3 && hasNavigatedFromRequiredClick) {
        updateFollowingPrefillStep(true, true, false)
      }
      if (!hasNavigatedFromRequiredClick) {
        updateFollowingPrefillStep(false, false, false)
      }
    }
  }

  const handleEnterKeyPress = (e: KeyboardEvent) => {
    if (e.code === 'Enter' && answer) {
      nextButtonCb(nextQuestion)
    }
  }

  const updateAnswer = (value: string) => {
    setAnswer(value)
    if (removeContinueCtaV1) {
      autoAdvance(value ?? defaultAnswer)
    }
  }

  const renderOptions = () => {
    if (msmThemeV1) {
      return (
        <ToggleButtonGroup
          options={SimpleDrivingLicenceOptions}
          id="driving-licence-options"
          defaultState={answer || preselectedValue}
          valueChanged={(value) => updateAnswer(value.toString())}
        />
      )
    }
    return (
      <RadioButton
        options={SimpleDrivingLicenceOptions}
        id="driving-licence-options"
        direction="vertical"
        mb={6}
        mt={3}
        valueChanged={(value) => updateAnswer(value.toString())}
        onKeyPress={handleEnterKeyPress}
        defaultState={answer || preselectedValue}
        checkedVariant="fill"
        variant={determineRadioVariant({
          smallRadiosSideBySide: false
        })}
        alignment="center"
        removeContinueCta={removeContinueCtaV1}
      />
    )
  }

  return (
    <>
      <GridItem
        gridColumn={['auto / span 12', '3 / span 8']}
        minHeight="174px"
      >
        <AppFormQuestion
          questionKey={QuestionKey.DRIVING_LICENCE}
          mt={3}
          textStyle={questionFontStyle}
        >
          {questionCopyChangeV1 ? 'Do you have a full UK driving licence?' : 'What type of licence do you have?'}
        </AppFormQuestion>
        {renderOptions()}
      </GridItem>
      <NavigationButtons
        nextHandler={() => nextButtonCb(nextQuestion)}
        previousHandler={() => previousButtonCb(previousQuestion)}
        disableNext={false}
        disablePrevious={disablePrevious ? 'invisible' : undefined}
        hideNext={removeContinueCtaV1}
      />
    </>
  )
}

export default withFade(withQuestion(DoubleColumnRadioDrivingLicenceQuestion))
